import checkoutApi from 'services/api/checkout';
import checkoutUtils from 'utils/Checkout';
import localeUtils from 'utils/LanguageLocale';
import GooglePayEN from 'utils/locales/GooglePay_en_US';
import GooglePayFR from 'utils/locales/GooglePay_fr_CA';

/**
 * @typedef {Object} IntermediateAddress
 * @property {string} [countryCode]
 * @property {string} [postalCode]
 * @property {string} [administrativeArea]
 * @property {string} [locality]
 * @property {string} [sortingCode]
 */

/**
 * @typedef {Object} IntermediatePaymentData
 * @property {('SHIPPING_ADDRESS'|'SHIPPING_OPTION'|'INITIALIZE')} callbackTrigger
 * @property {IntermediateAddress} [shippingAddress]
 * @property {{id: string}} [shippingOptionData]
 */

/**
 * @typedef {Object} CheckoutContext
 * @property {string|number} orderId
 * @property {string|number} shippingGroupId
 * @property {Array} [shippingMethods]
 * @property {Object} [priceInfo]
 * @property {string|number} [currentShippingMethodId]
 * @property {boolean} [hasShippingOptions] - true for STH carts, false for SDD/Mixed
 * @property {string} [transactionCurrencyCode]
 */

const CALLBACK_TRIGGERS = {
    SHIPPING_ADDRESS: 'SHIPPING_ADDRESS',
    SHIPPING_OPTION: 'SHIPPING_OPTION',
    INITIALIZE: 'INITIALIZE'
};

const ERROR_REASONS = {
    OTHER_ERROR: 'OTHER_ERROR'
};

const ERROR_INTENTS = {
    SHIPPING_ADDRESS: 'SHIPPING_ADDRESS',
    SHIPPING_OPTION: 'SHIPPING_OPTION',
    PAYMENT_AUTHORIZATION: 'PAYMENT_AUTHORIZATION'
};

const TRANSACTION_STATES = {
    SUCCESS: 'SUCCESS',
    ERROR: 'ERROR'
};

const TOTAL_PRICE_STATUS = {
    // Google Pay docs recommend ESTIMATED for intermediate callback updates;
    // FINAL is reserved for the final authorized total returned to the paysheet.
    ESTIMATED: 'ESTIMATED'
};

// 3-state cart classification used in the idempotency cache key. UNKNOWN keeps
// callers that forget hasShippingOptions from colliding with a real SDD/Mixed
// entry (false) — see buildCacheKey for the why.
const CART_TYPES = {
    STH: 'STH',
    SDD: 'SDD',
    UNKNOWN: 'UNKNOWN'
};

const CACHE_TTL_MS = 30000;

// Module-level cache for INITIALIZE idempotency. Skips duplicate Cart Service
// calls when the paysheet re-invokes INITIALIZE with the same address (a known
// Google Pay behavior — fires multiple times on init/account change).
let lastRequestHash = null;
let lastResponse = null;
let lastResponseTimestamp = 0;

/**
 * Stable hash for an address used to dedupe INITIALIZE invocations. Uses only
 * the partial fields Google Pay provides during onPaymentDataChanged
 * (country/postal/state/city) — street/name/phone are not available here.
 *
 * @param {IntermediateAddress} address
 * @returns {string}
 */
function hashAddress(address) {
    if (!address) {
        return '';
    }

    return [address.countryCode, address.postalCode, address.administrativeArea, address.locality]
        .map(part => (part || '').toString().trim().toUpperCase())
        .join('|');
}

/**
 * Composite cache key combining address + session-identifying context. Prevents
 * a previous session's cached response from being returned when the new
 * session has a different order, shipping group, currency, or cart type.
 *
 * @param {IntermediateAddress} address
 * @param {CheckoutContext} context
 * @returns {string}
 */
function buildCacheKey(address, context = {}) {
    // Explicit 3-state segment so an omitted hasShippingOptions does NOT collide
    // with an SDD/Mixed cart (false). Falling back to SDD would let an STH
    // caller that forgot the flag hit an SDD-shaped cached response.
    let cartType;

    if (context.hasShippingOptions === true) {
        cartType = CART_TYPES.STH;
    } else if (context.hasShippingOptions === false) {
        cartType = CART_TYPES.SDD;
    } else {
        cartType = CART_TYPES.UNKNOWN;
    }

    return [hashAddress(address), context.orderId, context.shippingGroupId, context.transactionCurrencyCode, cartType].join('::');
}

/**
 * Maps Google Pay's IntermediateAddress shape to Sephora's Cart Service order
 * address shape. Mirrors formatPayPalShippingAddress: uppercases city/country,
 * trims postal code. Street/name/phone are not provided during
 * onPaymentDataChanged (only on onPaymentAuthorized), so they default to empty.
 *
 * @param {IntermediateAddress} intermediateAddress
 * @returns {Object} Address in checkoutApi.createShippingAddress shape
 */
function normalizeGooglePayAddress(intermediateAddress = {}) {
    return {
        firstName: '',
        lastName: '',
        address1: '',
        address2: '',
        city: (intermediateAddress.locality || '').toUpperCase(),
        state: intermediateAddress.administrativeArea || '',
        postalCode: (intermediateAddress.postalCode || '').trim(),
        country: (intermediateAddress.countryCode || '').toUpperCase(),
        phone: ''
    };
}

/**
 * Localised, fallback-safe getter for the generic error message returned to
 * the paysheet when Cart Service fails without a surfaceable message.
 *
 * @returns {string}
 */
function getGenericErrorMessage() {
    const getText = localeUtils.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);

    return getText('googlePayGenericError');
}

/**
 * Builds Google Pay's PaymentDataError shape. The paysheet renders the message
 * inline; reason/intent drive its built-in error UX.
 *
 * @param {string} message
 * @param {string} [intent=SHIPPING_ADDRESS]
 * @returns {{ error: { reason: string, message: string, intent: string } }}
 */
function buildPaymentDataError(message, intent = ERROR_INTENTS.SHIPPING_ADDRESS) {
    return {
        error: {
            reason: ERROR_REASONS.OTHER_ERROR,
            message: message || getGenericErrorMessage(),
            intent
        }
    };
}

/**
 * Builds Google Pay's PaymentAuthorizationResult ERROR shape. Pinned to the
 * PAYMENT_AUTHORIZATION intent (the only intent valid for onPaymentAuthorized).
 * The paysheet renders the message inline and stays open so the customer can
 * pick a different card and retry.
 *
 * @param {string} message
 * @returns {{ transactionState: string, error: { reason: string, message: string, intent: string } }}
 */
function buildPaymentAuthorizationError(message) {
    return {
        transactionState: TRANSACTION_STATES.ERROR,
        error: {
            reason: ERROR_REASONS.OTHER_ERROR,
            message: message || getGenericErrorMessage(),
            intent: ERROR_INTENTS.PAYMENT_AUTHORIZATION
        }
    };
}

/**
 * Builds Google Pay's newTransactionInfo. Per the ticket, the paysheet main
 * line shows TOTAL only — do NOT include displayItems. totalPriceStatus is
 * ESTIMATED because this runs during onPaymentDataChanged callbacks (the
 * total is still subject to further changes before authorization). Falls back
 * to '0.00' when orderTotal is missing because Google Pay rejects an empty
 * or non-numeric totalPrice string with a paysheet error.
 *
 * @param {Object} priceInfo - from getOrderDetails (orderTotal lives here)
 * @param {string} transactionCurrencyCode - ISO 4217 (e.g. 'USD', 'CAD')
 * @returns {Object} newTransactionInfo
 */
function buildTransactionInfo(priceInfo = {}, transactionCurrencyCode) {
    const rawTotal = priceInfo.orderTotal;
    const numericTotal = Number(rawTotal);
    // Google Pay rejects non-numeric or empty totalPrice strings. Force a
    // 2-fraction-digit decimal string (e.g. "125.50" not "125.5") which is
    // what its spec expects for money values.
    // isFinite (not !isNaN) — guards against Infinity/-Infinity too, otherwise
    // Infinity.toFixed(2) throws RangeError and crashes the handler.
    const totalPrice = rawTotal != null && Number.isFinite(numericTotal) ? numericTotal.toFixed(2) : '0.00';
    const isCanada = localeUtils.isCanada();
    // Fall back to the locale's default currency when the caller didn't supply
    // one — Google Pay rejects an undefined currencyCode and aborts the paysheet.
    const currencyCode = transactionCurrencyCode || (isCanada ? 'CAD' : 'USD');

    return {
        countryCode: isCanada ? 'CA' : 'US',
        currencyCode,
        totalPriceStatus: TOTAL_PRICE_STATUS.ESTIMATED,
        totalPrice
    };
}

/**
 * Builds Google Pay's newShippingOptionParameters from Cart Service shipping
 * methods. The default flag from Cart Service drives defaultSelectedOptionId;
 * if none flagged, falls back to the first option to avoid an empty selection.
 *
 * @param {Array} shippingMethods - from checkoutApi.getAvailableShippingMethods
 * @returns {{ defaultSelectedOptionId: string, shippingOptions: Array }}
 */
function buildShippingOptionParameters(shippingMethods = []) {
    const shippingOptions = shippingMethods.map(method => ({
        id: String(method.shippingMethodId),
        label: method.shippingMethodType,
        description: [method.shippingMethodDescription, checkoutUtils.setShippingFee(method.shippingFee)].filter(Boolean).join(' — ')
    }));

    const defaultMethod = shippingMethods.find(method => method.isDefault) || shippingMethods[0];
    const defaultSelectedOptionId = defaultMethod ? String(defaultMethod.shippingMethodId) : '';

    return { defaultSelectedOptionId, shippingOptions };
}

/**
 * Resets the INITIALIZE idempotency cache. Called when a new paysheet session
 * starts (startCheckout) so a previous session's response is not reused.
 */
function resetCache() {
    lastRequestHash = null;
    lastResponse = null;
    lastResponseTimestamp = 0;
}

function getCachedResponse(hash) {
    if (!hash || hash !== lastRequestHash || !lastResponse) {
        return null;
    }

    if (Date.now() - lastResponseTimestamp > CACHE_TTL_MS) {
        // Drop the stale entry so it doesn't linger in memory or surface during
        // debugging of subsequent sessions.
        resetCache();

        return null;
    }

    // Return a deep copy so any downstream mutation of the response object
    // cannot corrupt the cached entry that later callers will read.
    return structuredClone(lastResponse);
}

function setCachedResponse(hash, response) {
    lastRequestHash = hash;
    // Store a deep copy so a later mutation of the caller's response object
    // cannot corrupt the cache entry that future hits will read.
    lastResponse = structuredClone(response);
    lastResponseTimestamp = Date.now();
}

/**
 * Handles a shipping address change. Composes existing Cart Service endpoints:
 *   createShippingAddress → (STH only) getAvailableShippingMethods →
 *   getOrderDetails. STH carts return newShippingOptionParameters with the
 *   Cart Service default flagged; SDD/Mixed carts return total only.
 *
 * @param {IntermediateAddress} intermediateAddress
 * @param {CheckoutContext} context
 * @returns {Promise<Object>} PaymentDataRequestUpdate
 */
async function handleShippingAddressChange(intermediateAddress, context) {
    if (!context?.shippingGroupId || !context?.orderId) {
        return buildPaymentDataError(getGenericErrorMessage(), ERROR_INTENTS.SHIPPING_ADDRESS);
    }

    const address = normalizeGooglePayAddress(intermediateAddress);

    // Cart Service expects { shippingGroupId, address, saveToProfile } — passing
    // the flat address alone returns errorFields: ['address', 'shippingGroupId'].
    await checkoutApi.createShippingAddress({
        shippingGroupId: context.shippingGroupId,
        address,
        saveToProfile: false
    });

    let shippingOptionParameters;

    // Strict === true so an omitted/undefined flag is NOT treated as STH and
    // does NOT silently skip the methods fetch (mirrors buildCacheKey's
    // 3-state semantics — STH | SDD | UNKNOWN).
    if (context.hasShippingOptions === true) {
        const methodsResponse = await checkoutApi.getAvailableShippingMethods(context.orderId, context.shippingGroupId);
        const shippingMethods = methodsResponse?.shippingMethods || [];

        shippingOptionParameters = buildShippingOptionParameters(shippingMethods);
    }

    const orderDetails = await checkoutApi.getOrderDetails(context.orderId);

    const update = {
        newTransactionInfo: buildTransactionInfo(orderDetails?.priceInfo, context.transactionCurrencyCode)
    };

    if (shippingOptionParameters) {
        update.newShippingOptionParameters = shippingOptionParameters;
    }

    return update;
}

/**
 * Handles a shipping option (method) change. Calls setShippingMethod and
 * returns total only — the option list itself does not change.
 *
 * @param {string} shippingMethodId
 * @param {CheckoutContext} context
 * @returns {Promise<Object>} PaymentDataRequestUpdate (newTransactionInfo only)
 */
async function handleShippingOptionChange(shippingMethodId, context) {
    if (!context?.shippingGroupId || !context?.orderId) {
        return buildPaymentDataError(getGenericErrorMessage(), ERROR_INTENTS.SHIPPING_OPTION);
    }

    await checkoutApi.setShippingMethod({
        orderId: context.orderId,
        shippingGroupId: context.shippingGroupId,
        shippingMethodId
    });

    const orderDetails = await checkoutApi.getOrderDetails(context.orderId);

    return {
        newTransactionInfo: buildTransactionInfo(orderDetails?.priceInfo, context.transactionCurrencyCode)
    };
}

/**
 * Handles the INITIALIZE trigger. Idempotent: returns a cached response for a
 * repeated address, and returns an empty update when no address is present
 * (lets the paysheet keep its original PaymentDataRequest).
 *
 * @param {IntermediatePaymentData} intermediatePaymentData
 * @param {CheckoutContext} context
 * @returns {Promise<Object>} PaymentDataRequestUpdate (possibly empty)
 */
async function handleInitialize(intermediatePaymentData, context) {
    const address = intermediatePaymentData?.shippingAddress;

    if (!address) {
        return {};
    }

    const cacheKey = buildCacheKey(address, context);
    const cached = getCachedResponse(cacheKey);

    if (cached) {
        return cached;
    }

    const response = await handleShippingAddressChange(address, context);

    setCachedResponse(cacheKey, response);

    return response;
}

/**
 * Shared dispatcher for Google Pay's onPaymentDataChanged callback. Wired in
 * by PAY-6706 (STH) and PAY-6708 (SDD/Mix). Returns a PaymentDataRequestUpdate
 * on success or a PaymentDataError on failure — never throws so the paysheet
 * stays in a recoverable state.
 *
 * @param {IntermediatePaymentData} intermediatePaymentData
 * @param {CheckoutContext} context - resolved by the caller (orderId,
 *   shippingGroupId, hasShippingOptions, transactionCurrencyCode)
 * @returns {Promise<Object>} PaymentDataRequestUpdate or PaymentDataError
 */
async function onPaymentDataChanged(intermediatePaymentData, context) {
    try {
        if (!intermediatePaymentData || !context) {
            return buildPaymentDataError(getGenericErrorMessage());
        }

        const trigger = intermediatePaymentData.callbackTrigger;

        if (trigger === CALLBACK_TRIGGERS.SHIPPING_ADDRESS) {
            return await handleShippingAddressChange(intermediatePaymentData.shippingAddress, context);
        }

        if (trigger === CALLBACK_TRIGGERS.SHIPPING_OPTION) {
            const shippingMethodId = intermediatePaymentData.shippingOptionData?.id;

            if (!shippingMethodId) {
                return buildPaymentDataError(getGenericErrorMessage(), ERROR_INTENTS.SHIPPING_OPTION);
            }

            return await handleShippingOptionChange(shippingMethodId, context);
        }

        if (trigger === CALLBACK_TRIGGERS.INITIALIZE) {
            return await handleInitialize(intermediatePaymentData, context);
        }

        // Unknown trigger — return empty update so the paysheet keeps original state
        return {};
    } catch (err) {
        Sephora?.logger?.error('[GooglePayHandlers] onPaymentDataChanged failed', err);

        const surfacedMessage = err?.errorMessage || err?.message;
        const isOptionTrigger = intermediatePaymentData?.callbackTrigger === CALLBACK_TRIGGERS.SHIPPING_OPTION;
        const intent = isOptionTrigger ? ERROR_INTENTS.SHIPPING_OPTION : ERROR_INTENTS.SHIPPING_ADDRESS;

        return buildPaymentDataError(surfacedMessage, intent);
    }
}

/**
 * Cart Service rejects with an `errorMessages` array of localized strings
 * Joins them with '. ' into a single string for the paysheet's inline message.
 *
 * @param {Object} err
 * @returns {string|null}
 */
function surfaceCartError(err) {
    if (Array.isArray(err?.errorMessages) && err.errorMessages.length > 0) {
        return err.errorMessages.join('. ');
    }

    return err?.errorMessage || err?.message || null;
}

/**
 * Shared Google Pay onPaymentAuthorized handler. Submits the full PaymentData
 * directly under the `googlePay` body key, maps the response to Google Pay's
 * PaymentAuthorizationResult.
 *
 * TODO: GOOGLE: PAY-6759 (BOPIS) / PAY-6709 (SDD/Mix) will each replace
 * their per-flow `on*PaymentAuthorized` scaffold body with a one-line
 * delegation to this function. STH is wired by PAY-6707.
 *
 * `paymentData` is passed through whole (NOT nested under a `paymentData`
 * key) so Cart Service can read shippingAddress/email/billingAddress/
 * tokenizationData at the same depth they appear in the SDK response. Paze's
 * `paze: { checkoutResponse, completeResponse }` wraps because Paze returns
 * two SDK responses; Google Pay returns one, so no wrapper is needed.
 *
 * Side effect on success: writes the placeOrder response to
 * `context.orderResponse` so the per-flow wrapper can read it after the
 * paysheet closes and pass it to `checkoutUtils.placeOrderSuccess` for the
 * confirmation-page redirect (PAY-6707).
 *
 * @param {Object} paymentData - Google Pay SDK PaymentData
 * @param {CheckoutContext} context - checkout context (orderId at minimum)
 * @returns {Promise<{ transactionState: string, error?: Object }>}
 */
async function onPaymentAuthorized(paymentData, context) {
    try {
        const response = await checkoutApi.placeOrder({
            orderId: context?.orderId,
            originOfOrder: 'web',
            isExpressCheckout: true,
            googlePay: paymentData
        });

        if (context) {
            context.orderResponse = response;
        }

        return { transactionState: TRANSACTION_STATES.SUCCESS };
    } catch (err) {
        Sephora?.logger?.error('[GooglePayHandlers] onPaymentAuthorized failed', err);

        const message = surfaceCartError(err) || getGenericErrorMessage();

        return buildPaymentAuthorizationError(message);
    }
}

export {
    CALLBACK_TRIGGERS,
    ERROR_REASONS,
    ERROR_INTENTS,
    TRANSACTION_STATES,
    CART_TYPES,
    onPaymentDataChanged,
    onPaymentAuthorized,
    resetCache,
    // Exported for testing
    normalizeGooglePayAddress,
    hashAddress,
    buildCacheKey,
    buildPaymentDataError,
    buildPaymentAuthorizationError,
    buildTransactionInfo,
    buildShippingOptionParameters,
    handleShippingAddressChange,
    handleShippingOptionChange,
    handleInitialize,
    surfaceCartError
};
